/**
 * @velyn-custom BEGIN — vely-quickview-speed.js
 * Speeds up accessory Details quick view without editing Core theme.js.
 *
 * Core ButtonQuickView.init() binds onClick at construct time, so replacing
 * prototype.onClick never runs for existing buttons. Core also waits for extra
 * JS modules, then fetches product-quickview HTML, then opens the drawer.
 *
 * This layer:
 * - opens the drawer on click (capture) immediately
 * - prefetches HTML on hover / idle
 * - serves cached HTML to Core fetchContent (dynamic lookup, so this patch works)
 */
(function () {
  if (window.__velyQuickViewSpeedBound) return;
  window.__velyQuickViewSpeedBound = true;

  const htmlCache = new Map();
  const inflight = new Map();
  const DETAILS_SELECTOR = ".vely-pdp-accessory__details[is='button-quick-view']";

  function cacheKey(url, sectionId) {
    return String(url || "") + "?section_id=" + String(sectionId || "");
  }

  function drawerSectionId() {
    return document.querySelector("quickview-drawer")?.dataset.sectionId || "";
  }

  function prefetchQuickview(url, sectionId) {
    if (!url || !sectionId) return Promise.resolve("");
    const key = cacheKey(url, sectionId);
    if (htmlCache.has(key)) return Promise.resolve(htmlCache.get(key));
    if (inflight.has(key)) return inflight.get(key);

    const request = fetch(key, { credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error("Quick view fetch failed");
        return response.text();
      })
      .then((text) => {
        htmlCache.set(key, text);
        inflight.delete(key);
        return text;
      })
      .catch((error) => {
        inflight.delete(key);
        throw error;
      });

    inflight.set(key, request);
    return request;
  }

  function injectQuickviewHtml(text) {
    const source = new DOMParser()
      .parseFromString(text, "text/html")
      .querySelector(".quickview-product");
    const dest = document.querySelector(".quickview-product");
    if (!source || !dest) return false;

    // Never replace real QV markup with an empty shell response.
    const sourceHasProduct = Boolean(
      source.querySelector("[data-vely-pdp-section]")
    );
    const destHasProduct = Boolean(
      dest.querySelector("[data-vely-pdp-section]")
    );
    if (!sourceHasProduct && destHasProduct) return false;
    if (!sourceHasProduct && !destHasProduct) return false;

    dest.innerHTML = source.innerHTML;
    hydrateQuickview(dest);
    return true;
  }

  function hydrateQuickview(root) {
    if (!root) return;
    const drawer =
      root.closest?.("quickview-drawer.vely-product-quickview") ||
      document.querySelector("quickview-drawer.vely-product-quickview");

    if (typeof window.velyBindPdpSection === "function") {
      root.querySelectorAll("[data-vely-pdp-section]").forEach((section) => {
        delete section.dataset.velyPdpSectionBound;
        window.velyBindPdpSection(section);
      });
    }

    // Defer images while closed; promote only when drawer is open.
    if (drawer && !drawer.classList.contains("active")) {
      if (typeof window.velyDemoteQuickviewLazyImages === "function") {
        window.velyDemoteQuickviewLazyImages(drawer);
      }
    } else if (typeof window.velySyncQuickviewImages === "function") {
      window.velySyncQuickviewImages(drawer || root);
    } else if (typeof window.velyEnhanceQuickviewGallery === "function") {
      window.velyEnhanceQuickviewGallery(root);
    }
  }

  function warmQuickviewModules() {
    const js = window.importJs;
    if (!js || window.__velyQuickViewModulesWarmed) return;
    window.__velyQuickViewModulesWarmed = true;
    Promise.allSettled([
      js.mediaGallery ? import(js.mediaGallery) : null,
      js.productModel ? import(js.productModel) : null,
      js.mediaLightboxGallery ? import(js.mediaLightboxGallery) : null,
    ]);
  }

  function prefetchVisibleDetails() {
    const sectionId = drawerSectionId();
    if (!sectionId) return;
    document.querySelectorAll(DETAILS_SELECTOR).forEach((button, index) => {
      if (index > 3 || !button.dataset.url) return;
      prefetchQuickview(button.dataset.url, sectionId);
    });
  }

  function patchButtonQuickView() {
    const QuickView = customElements.get("button-quick-view");
    if (!QuickView || QuickView.prototype.__velySpeedPatched) return;
    QuickView.prototype.__velySpeedPatched = true;

    const originalFetchContent = QuickView.prototype.fetchContent;

    QuickView.prototype.fetchContent = async function velyQuickViewFetchContent() {
      const url = this.dataset.url;
      const sectionId = this.sectionId;
      const key = cacheKey(url, sectionId);
      const drawer = this.drawer;

      if (!htmlCache.has(key)) {
        try {
          await prefetchQuickview(url, sectionId);
        } catch (error) {
          drawer?.classList.remove("vely-quickview-loading");
          throw error;
        }
      }

      const cached = htmlCache.get(key);
      const originalFetch = window.fetch.bind(window);
      window.fetch = function velyCachedFetch(input, init) {
        const href = typeof input === "string" ? input : input?.url || "";
        if (
          cached &&
          href.indexOf("section_id=" + sectionId) !== -1 &&
          href.indexOf(url) !== -1
        ) {
          return Promise.resolve(
            new Response(cached, {
              status: 200,
              headers: { "Content-Type": "text/html" },
            })
          );
        }
        return originalFetch(input, init);
      };

      try {
        const result = await originalFetchContent.call(this);
        // If Core wrote an empty shell, restore from cache when possible.
        const dest = document.querySelector(".quickview-product");
        if (
          dest &&
          !dest.querySelector("[data-vely-pdp-section]") &&
          cached
        ) {
          injectQuickviewHtml(cached);
        } else {
          hydrateQuickview(dest);
        }
        return result;
      } finally {
        window.fetch = originalFetch;
        drawer?.classList.remove("vely-quickview-loading");
      }
    };
  }

  customElements.whenDefined("button-quick-view").then(patchButtonQuickView);
  if (customElements.get("button-quick-view")) patchButtonQuickView();

  document.addEventListener(
    "click",
    (event) => {
      const anyQuickView = event.target.closest?.("[is='button-quick-view']");
      const drawer = document.querySelector("quickview-drawer");
      if (anyQuickView && drawer) {
        // PDP accessories: details-only (no ATC).
        // Cart drawer / cart page: Core QV with ATC, without payment-button.
        if (anyQuickView.hasAttribute("data-vely-details-only")) {
          drawer.setAttribute("data-vely-details-only", "true");
        } else {
          drawer.removeAttribute("data-vely-details-only");
        }
        if (anyQuickView.hasAttribute("data-vely-hide-payment")) {
          drawer.setAttribute("data-vely-hide-payment", "true");
        } else {
          drawer.removeAttribute("data-vely-hide-payment");
        }
      }

      const trigger = event.target.closest?.(DETAILS_SELECTOR);
      if (!trigger?.dataset.url || !drawer) return;

      const sectionId = trigger.sectionId || drawer.dataset.sectionId;
      drawer.classList.add("vely-quickview-loading");
      warmQuickviewModules();

      const htmlPromise = prefetchQuickview(trigger.dataset.url, sectionId);
      if (typeof trigger.openDrawer === "function") {
        trigger.openDrawer();
      }

      htmlPromise
        .then((text) => {
          // Inject whenever the drawer is open. Do not require the loading class:
          // Core fetchContent may clear vely-quickview-loading first and skip inject,
          // which left cart/mini-cart Details on an empty gray drawer.
          const drawerOpen =
            drawer.classList.contains("active") ||
            drawer.classList.contains("vely-quickview-loading");
          if (!drawerOpen) return;
          injectQuickviewHtml(text);
          drawer.classList.remove("vely-quickview-loading");
        })
        .catch(() => {
          drawer.classList.remove("vely-quickview-loading");
        });
    },
    true
  );

  document.addEventListener(
    "pointerenter",
    (event) => {
      const trigger = event.target.closest?.(DETAILS_SELECTOR);
      if (!trigger?.dataset.url) return;
      warmQuickviewModules();
      prefetchQuickview(trigger.dataset.url, drawerSectionId());
    },
    true
  );

  document.addEventListener(
    "focusin",
    (event) => {
      const trigger = event.target.closest?.(DETAILS_SELECTOR);
      if (!trigger?.dataset.url) return;
      warmQuickviewModules();
      prefetchQuickview(trigger.dataset.url, drawerSectionId());
    },
    true
  );

  const schedulePrefetch = () => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(prefetchVisibleDetails, { timeout: 2500 });
    } else {
      setTimeout(prefetchVisibleDetails, 400);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedulePrefetch, { once: true });
  } else {
    schedulePrefetch();
  }

  document.addEventListener("shopify:section:load", () => {
    schedulePrefetch();
    observeAccessoriesHosts();
  });

  function observeAccessoriesHosts() {
    document.querySelectorAll("vely-pdp-accessories").forEach((host) => {
      if (host.__velyQvObserved) return;
      host.__velyQvObserved = true;
      new MutationObserver(schedulePrefetch).observe(host, {
        childList: true,
        subtree: true,
      });
    });
  }

  observeAccessoriesHosts();
})();
/* @velyn-custom END — vely-quickview-speed.js */
